/**
 * 설정 › 구독 티어 (관리자).
 *
 * 부여 표는 코드가 아니라 **설정**이다(docs/subscription-tiers.md 결정 ⑦). 요금제를 조정할
 * 때마다 배포하면 안 되고, 특정 고객에게 기능 하나만 열어 주는 일도 생긴다. QBase 의 메뉴
 * 권한 부여와 같은 모양 — 기능 목록은 코드가, **부여는 데이터**가 갖는다.
 *
 * **표에 있는 것만 끌 수 있다.** SQL 가드·변조 검증·DDL 승인 같은 안전장치는 카탈로그에
 * 등장조차 하지 않으므로 관리자가 실수로도 못 끈다. `alwaysOn` 기능은 보이되 잠겨 있다 —
 * 없으면 SQL 자체를 못 만든다.
 *
 * 관리자가 아니면 서버가 403 을 주고, 이 항목은 설정 메뉴에서 아예 보이지 않는다.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FiRefreshCw, FiLock } from 'react-icons/fi';
import * as api from '../../services/assistantClient';

function TierAdminSection() {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [grantUsrid, setGrantUsrid] = useState('');
    const [grantTier, setGrantTier] = useState('');

    const refresh = useCallback(async () => {
        try {
            const d = await api.getTierAdmin();
            setData(d);
            setGrantTier(prev => prev || (d.tiers[0] && d.tiers[0].id) || '');
        } catch (err) {
            toast.error(err.message);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    // 체크는 **화면에서 먼저 반영하고** 서버에 보낸다. 표가 크면 왕복마다 깜빡여 어디를 눌렀는지 잃는다.
    const toggle = async (tier, featureId, on) => {
        const next = on
            ? [...new Set([...tier.features, featureId])]
            : tier.features.filter(f => f !== featureId);
        setData(d => ({ ...d, tiers: d.tiers.map(x => (x.id === tier.id ? { ...x, features: next } : x)) }));
        setBusy(true);
        try {
            const r = await api.setTierFeatures(tier.id, next);
            // 서버가 카탈로그에 없는 id 를 버리므로 **서버가 준 것**으로 맞춘다
            setData(d => ({ ...d, tiers: d.tiers.map(x => (x.id === tier.id ? { ...x, features: r.features } : x)) }));
        } catch (err) {
            toast.error(err.message);
            await refresh();                       // 실패하면 화면을 서버에 맞춘다
        } finally { setBusy(false); }
    };

    const saveLimits = async (tier, patch) => {
        setBusy(true);
        try {
            const r = await api.setTierLimits(tier.id, { ...patch });
            setData(d => ({
                ...d,
                tiers: d.tiers.map(x => (x.id === tier.id
                    ? { ...x, maxResultRows: r.maxResultRows, maxExecutionMs: r.maxExecutionMs } : x)),
            }));
            toast.success(t('assistant:tier.limitsSaved'));
        } catch (err) {
            toast.error(err.message);
        } finally { setBusy(false); }
    };

    const grant = async () => {
        const id = grantUsrid.trim();
        if (!id || !grantTier) return;
        setBusy(true);
        try {
            await api.setUserTier(id, grantTier);
            setGrantUsrid('');
            toast.success(t('assistant:tier.granted', { usrid: id, tier: grantTier }));
            await refresh();
        } catch (err) {
            toast.error(err.message);
        } finally { setBusy(false); }
    };

    if (!data) return <div className="stg-row"><span className="stg-hint">{t('assistant:tier.loading')}</span></div>;

    return (
        <section>
            <div className="stg-row">
                <div className="stg-row-main">
                    <span className="stg-label">{t('assistant:tier.tableLabel')}</span>
                    <span className="stg-hint">{t('assistant:tier.tableHint')}</span>
                </div>
                <button type="button" className="app-btn" onClick={refresh} disabled={busy}>
                    <FiRefreshCw size={12} /> {t('assistant:tier.refresh')}
                </button>
            </div>

            <div className="stg-table-wrap">
                <table className="stg-table">
                    <thead>
                        <tr>
                            <th className="stg-left">{t('assistant:tier.featureCol')}</th>
                            {data.tiers.map(tier => <th key={tier.id}>{t(`assistant:tier.name.${tier.id}`, tier.id)}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {data.catalog.map(f => (
                            <tr key={f.id}>
                                <td className="stg-left">
                                    {t(`assistant:tier.feature.${f.id}`, f.id)}
                                    {f.alwaysOn && (
                                        <span className="stg-hint" title={t('assistant:tier.alwaysOnHint')}>
                                            {' '}<FiLock size={10} />
                                        </span>
                                    )}
                                </td>
                                {data.tiers.map(tier => (
                                    <td key={tier.id} className="stg-center">
                                        <input
                                            type="checkbox"
                                            // 없으면 SQL 자체를 못 만드는 기능이라 끌 수 없다
                                            checked={f.alwaysOn || tier.features.includes(f.id)}
                                            disabled={busy || f.alwaysOn}
                                            aria-label={`${tier.id} / ${f.id}`}
                                            onChange={(e) => toggle(tier, f.id, e.target.checked)}
                                        />
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="stg-row">
                <div className="stg-row-main">
                    <span className="stg-label">{t('assistant:tier.limitsLabel')}</span>
                    <span className="stg-hint">{t('assistant:tier.limitsHint')}</span>
                </div>
            </div>
            {data.tiers.map(tier => (
                <div className="stg-row" key={`lim-${tier.id}`}>
                    <div className="stg-row-main">
                        <span className="stg-hint">{t(`assistant:tier.name.${tier.id}`, tier.id)}</span>
                    </div>
                    <span className="stg-inline">
                        <input
                            className="stg-text stg-number-wide" type="number" min={1}
                            defaultValue={tier.maxResultRows}
                            aria-label={t('assistant:tier.maxRows')}
                            onBlur={(e) => {
                                const v = Number(e.target.value);
                                if (v > 0 && v !== tier.maxResultRows) saveLimits(tier, { maxResultRows: v });
                            }}
                        />
                        <span className="stg-hint">{t('assistant:tier.maxRows')}</span>
                    </span>
                </div>
            ))}

            <div className="stg-row">
                <div className="stg-row-main">
                    <span className="stg-label">{t('assistant:tier.grantLabel')}</span>
                    <span className="stg-hint">{t('assistant:tier.grantHint')}</span>
                </div>
                <span className="stg-inline">
                    <input
                        className="stg-text" value={grantUsrid} placeholder="usrid"
                        aria-label={t('assistant:tier.grantLabel')}
                        onChange={(e) => setGrantUsrid(e.target.value)}
                    />
                    <select className="stg-select" value={grantTier} onChange={(e) => setGrantTier(e.target.value)}>
                        {data.tiers.map(tier => (
                            <option key={tier.id} value={tier.id}>{t(`assistant:tier.name.${tier.id}`, tier.id)}</option>
                        ))}
                    </select>
                    <button type="button" className="app-btn" onClick={grant} disabled={busy || !grantUsrid.trim()}>
                        {t('assistant:tier.grant')}
                    </button>
                </span>
            </div>

            {data.users.length > 0 && (
                <div className="stg-row">
                    <div className="stg-row-main">
                        <span className="stg-hint">
                            {data.users.slice(0, 10).map(u => `${u.usrid} → ${u.tierId}`).join(' · ')}
                        </span>
                    </div>
                </div>
            )}
        </section>
    );
}

export default TierAdminSection;
